import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DocusealBuilder } from "@docuseal/react";
import { Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DOCUMENT_SCOPES,
  getDocusealErrorMessage,
  type ContractDocument,
  type ContractKind,
  type DocumentScope,
} from "@/services/docuseal";
import { queryKeys } from "@/queryKeys";
import {
  BuilderViewport,
  DEFAULT_BUILDER_KIND,
  docusealBuilderFields,
  invalidateDocumentQueries,
  laneJurisdiction,
  TEMPLATE_PARTIES,
} from "@/pages/admin/documentPagesShared";
import {
  DOCUSEAL_APP_THEME_CSS,
  DOCUSEAL_BUILDER_TITLE_CSS,
  DOCUSEAL_HIDE_BUILDER_SAVE_CSS,
  DOCUSEAL_VARIABLES_TAB_CSS,
} from "@/pages/admin/documentBuilderTheme";
import { useBuilderBridge, useBuilderElement } from "@/pages/admin/useBuilderBridge";
import { useDraftTemplate } from "@/pages/admin/useDraftTemplate";
import DocumentConfigCard from "@/components/documents/DocumentConfigCard";
import SaveButton from "@/components/documents/SaveButton";
import TrialLocked from "@/components/trial/TrialLocked";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const DocumentNew: React.FC<{ scope?: DocumentScope }> = ({ scope = DOCUMENT_SCOPES.org }) => {
  const { service } = scope;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  // null until the admin picks one; Save waits for it.
  const [kind, setKind] = useState<ContractKind | null>(null);
  const [jurisdiction, setJurisdiction] = useState<string | null>(null);
  // Applied by Save with the rest of the form.
  const [enabled, setEnabled] = useState(false);
  // The type the builder opened with. Its Values palette stays on it, like the Edit page
  // before its first save, so changing the type here never remounts the builder.
  const [builderKind, setBuilderKind] = useState<ContractKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [builderHost, setBuilderHost] = useState<HTMLDivElement | null>(null);
  const builderElement = useBuilderElement(builderHost);
  const draft = useDraftTemplate(builderElement, service, {
    onDiscarded: () => void invalidateDocumentQueries(queryClient),
  });
  // No template id yet: the bridge follows the draft the builder creates.
  // A template may have no fields (sent as its raw PDF); the builder will not save that.
  const bridge = useBuilderBridge(builderElement, {
    templateId: null,
    clearFields: service.clearTemplateFields,
  });

  const seedsQuery = useQuery({
    queryKey: queryKeys.contractFieldSeeds(scope.key),
    queryFn: () => service.listFieldSeeds(),
    staleTime: Infinity,
  });
  const tokenMutation = useMutation({
    mutationFn: (templateName: string) => service.mintBuilderToken({ name: templateName }),
    onSuccess: () => setBuilderKind(kind ?? DEFAULT_BUILDER_KIND),
    onError: (err) => setError(getDocusealErrorMessage(err)),
  });

  const handleCancel = async () => {
    try {
      await draft.discard();
    } catch (err) {
      // Stay put: leaving would orphan a template the admin chose to throw away.
      setError(
        `Could not discard the draft: ${getDocusealErrorMessage(err)} Cancel again to retry.`
      );
      return;
    }
    navigate(scope.basePath);
  };

  // The page's one Save: saves the builder, applies the name, and keeps the draft.
  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Name is required.");
      if (!kind) throw new Error("Choose a type.");
      // Held before the first await so Cancel or unmount can never delete a template
      // the admin is saving. A failure below releases it.
      if (!draft.keep()) return null;
      try {
        const template = await bridge.save();
        const templateId = template.id ?? draft.id();
        if (templateId == null) throw new Error("The builder did not return a template id.");
        // The builder's save writes its loaded name back, so the name goes after it. One
        // call renames the template, verifies it is ours and saves the type and
        // on/off state as a row. The server makes it the default of an empty lane.
        return await service.registerDocument({
          kind,
          name: trimmed,
          docuseal_template_id: templateId,
          jurisdiction: laneJurisdiction(kind, jurisdiction),
          is_active: enabled,
          clean_template_fields: true,
        });
      } catch (err) {
        draft.release();
        throw err;
      }
    },
    onSuccess: async (row) => {
      if (row == null) return;
      // The edit page reads its type from the list, which may be cached without the new
      // row; it mounts from that first read.
      queryClient.setQueryData<ContractDocument[]>(
        queryKeys.contractAdminDocuments(scope.key),
        (old) => old && [...old, row]
      );
      await invalidateDocumentQueries(queryClient);
      navigate(`${scope.basePath}/${row.docuseal_template_id}/edit`);
    },
    onError: (err) => setError(getDocusealErrorMessage(err)),
  });

  const openBuilder = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setError(null);
    tokenMutation.mutate(trimmed);
  };

  const startSave = () => {
    setError(null);
    saveMutation.mutate();
  };

  const token = tokenMutation.data;
  const fields = docusealBuilderFields(
    seedsQuery.data?.[builderKind ?? kind ?? DEFAULT_BUILDER_KIND] ?? []
  );
  const saving = saveMutation.isPending;
  const saveDisabled = !token || saving || kind == null;

  return (
    <div className="space-y-6">
      <div>
        <Link to={scope.basePath} className="text-sm text-primary hover:underline">
          ← {scope.title}
        </Link>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">
          {token ? name.trim() || "New template" : "New template"}
        </h1>
      </div>

      {(error || seedsQuery.isError) && (
        <Alert variant="destructive">
          <AlertDescription>{error ?? getDocusealErrorMessage(seedsQuery.error)}</AlertDescription>
        </Alert>
      )}

      {/* The same card the edit page shows. Default waits for the first save; the type
          chosen when the builder opens is what its Values palette starts with. */}
      <DocumentConfigCard
        name={name}
        onNameChange={setName}
        kind={kind}
        onKindChange={setKind}
        jurisdiction={jurisdiction}
        onJurisdictionChange={setJurisdiction}
        enabled={enabled}
        enableDisabled={saving}
        onEnabledChange={setEnabled}
        isDefault={null}
        makeDefaultDisabled
        onMakeDefault={() => {}}
        saveDisabled={saveDisabled}
        dirty={bridge.dirty}
        saving={saving}
        onSave={startSave}
        headerAction={
          <Button type="button" variant="outline" size="sm" onClick={() => void handleCancel()}>
            Cancel
          </Button>
        }
      >
        {!token && (
          <TrialLocked lock="contractDocuments">
            <Button
              type="button"
              onClick={openBuilder}
              disabled={tokenMutation.isPending || !seedsQuery.data}
            >
              {tokenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Open builder
            </Button>
          </TrialLocked>
        )}
      </DocumentConfigCard>

      {token && (
        <Card>
          <CardContent className="pt-6">
            <BuilderViewport
              variableNames={fields.map((field) => field.name)}
              onHostChange={setBuilderHost}
              // The builder's own Save is hidden, so fullscreen carries the page's.
              fullscreenActions={
                <SaveButton
                  dirty={bridge.dirty}
                  saving={saving}
                  disabled={saveDisabled}
                  onClick={startSave}
                />
              }
            >
              <DocusealBuilder
                token={token.token}
                host={token.host}
                fields={fields}
                // A blank template has no party to keep in place: offer both, Merchant first.
                roles={TEMPLATE_PARTIES}
                withCustomFieldsTab={fields.length > 0}
                customCss={[
                  DOCUSEAL_APP_THEME_CSS,
                  DOCUSEAL_BUILDER_TITLE_CSS,
                  DOCUSEAL_HIDE_BUILDER_SAVE_CSS,
                  fields.length > 0 ? DOCUSEAL_VARIABLES_TAB_CSS : "",
                ]
                  .filter(Boolean)
                  .join("\n")}
                withSendButton={false}
                withSignYourselfButton={false}
                autosave={false}
                withTitle
                style={{ height: "100%", width: "100%" }}
              />
            </BuilderViewport>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DocumentNew;
